import { Suspense, lazy, memo, useCallback, useEffect, useState } from "react";
import { useMemo, useRef } from "react";
import { UploadOptions } from "@maysoft/tus-js-client";

import Box from "../Box";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import { Constants } from "../../constants/constants";
import { FileAccessMode, FileProcessingStatus, Mode } from "../../constants/enums";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import ResumableUploadService, { ResumableUploadRequest } from "../../services/asset/resumableUpload.service";

import { IJodit } from "jodit/types";
import { Props } from "../InputHtmlEditor";
import "../InputHtmlEditor/styles.css";
const JoditEditor = lazy(() => import("jodit-react"));

interface CustomProps extends Props {
    defaultValue?: string;
    imageResizable?: boolean;
    fileAccessMode?: FileAccessMode;
}

// CUSTOM INPUT HTML EDITOR (WITH IMAGE UPLOADER)
const InputHtmlUploadEditor: React.FC<CustomProps> = (props: CustomProps) => {
    // useTraceUpdate(props);

    const {
        serviceCode,
        tenantCode,
        onError,
        onShowLoading,
        onHideLoading,
    } = useCommonComponentContext();
    const strings = useStrings();
    const editor = useRef(null);

    useEffect(() => {
        import("jodit-react").then((Jodit) => {
            Jodit.Jodit.defaultOptions.controls.uploadImage = (() => {
                if (Helpers.isNullOrEmpty(serviceCode) ||
                    Helpers.isNullOrEmpty(tenantCode) ||
                    Helpers.isNullOrEmpty(strings) ||
                    !Helpers.isFunction(onShowLoading) || 
                    !Helpers.isFunction(onHideLoading) ||
                    !Helpers.isFunction(onError)
                ) {
                    return {};
                }
                return ({
                    name: `Upload ${strings.Common.IMAGE.toLowerCase()}`,
                    exec: (async (editor: any) => {
                        if (Helpers.isNullOrEmpty(editor)) {
                            return;
                        }
                        await handleImageUpload(
                            editor,
                            serviceCode,
                            tenantCode,
                            props?.fileAccessMode,
                            strings.Common.ERROR,
                            onError,
                            onShowLoading,
                            onHideLoading
                        );
                    })
                })
            })()
        });
    }, [serviceCode, tenantCode, props?.fileAccessMode, strings, onError, onShowLoading, onHideLoading]);

    // Prevent image pasting into Jodit
    const handleBeforePaste = useCallback((event: any) => {
        const items = (event.clipboardData || event.originalEvent.clipboardData).items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf("image") === 0) {
                onError && onError(strings.Common.HTML_EDITOR_CONTENT_ERROR);
                return false;
            }
        }

        const clipboardData = (event.clipboardData || event.originalEvent.clipboardData).getData("text/html");
        if (!clipboardData) {
            return;
        }

        const doc = new DOMParser().parseFromString(clipboardData, "text/html");
        const images = doc.querySelectorAll("img");
        if (images.length === 0) {
            return;
        }

        onError && onError(strings.Common.HTML_EDITOR_CONTENT_ERROR);
        return false;
    }, [strings]);

    const config = useMemo(() => {
        let allowResizeTags = ["table"];
        let resizer = { showSize: false };
        
        if (Helpers.isNullOrEmpty(props?.imageResizable) || props?.imageResizable === true) {
            allowResizeTags.push("img");
            resizer.showSize = true;
        }

        return {
            // value: props.value,
            height: props.maxHeight ?? "auto",
            readonly: props.mode === Mode.View || props.readonly, // all options from https://xdsoft.net/jodit/docs/,
            placeholder: props.placeholder ?? "",
            toolbar: props.mode === Mode.View || props.hideToolbar ? false : true,
            statusbar: props.mode === Mode.View || props.hideToolbar ? false : true,
            iframe: true, // enable iframe mode,
            events: {
                applyLink: (jodit: IJodit, link: HTMLElement) => {
                    if (!link) return;
                    link.setAttribute("nofollow", "true");
                    link.setAttribute("target", "_blank");
                },
                beforePaste: handleBeforePaste,
            },
            allowResizeTags,
            resizer,
            image: {
                crop: false,
                resize: false,
                useImageEditor: false,
                availableClasses: false,
                editBorderRadius: false,
                editClass: false,
                editId: false,
                editLink: false,
                editMargins: false,
                editSize: false,
                editSrc: false,
                editStyle: false,
            },
            askBeforePasteFromWord: false,
            askBeforePasteHTML: false,

            removeButtons: ["image", "video", "file", "preview"],
            extraButtons: ["|", "uploadImage"],
        };
    }, [props?.imageResizable, props?.maxHeight, props?.mode, props?.placeholder, handleBeforePaste])

    const [defaultValue, setDefaultValue] = useState<string>();
    useEffect(() => {
        if (!Helpers.isNullOrEmpty(props?.defaultValue) && Helpers.isNullOrEmpty(defaultValue)) {
            setDefaultValue(props?.defaultValue)
        }
    }, [props?.defaultValue, defaultValue])

    const typographyProps: any = useMemo(() => {
        if (props.mode === Mode.View)
            return {
                variant: "button",
                fontWeight: "bold",
                color: "dark",
            };
        return {
            variant: "caption",
            fontWeight: "regular",
            color: Helpers.isNullOrEmpty(props.errorMessage) ? "secondary" : "error",
        };
    }, [props.mode, props.errorMessage]);

    const handleImageUpload = async (
        editor: any,
        serviceCode: string,
        tenantCode: string,
        fileAccessMode: FileAccessMode | undefined,
        errorUploadMessage: string,
    
        onError: (message: string) => void,
        onShowLoading: () => void,
        onHideLoading: () => void,
    ) => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();
    
        input.onchange = async () => {
            const file = input.files ? input.files[0] : null;
    
            if (Helpers.isNullOrEmpty(file)) {
                return;
            }
    
            if (Helpers.isNullOrEmpty(editor)) {
                return;
            }
    
            // console.log(editor.selection.insertImage)
            
            let folderPath = Constants.CloudStorage.EDITOR_CONTENT_FOLDER;
            fileAccessMode = fileAccessMode ?? FileAccessMode.Public;

            if (fileAccessMode === FileAccessMode.Private) {
                folderPath = Constants.CloudStorage.TEMP_FOLDER;
            }

            try {
                onShowLoading();
                // editor.selection.focus();
    
                const request: ResumableUploadRequest = {
                    folderPath: folderPath,
                    accessMode: `${fileAccessMode}`,
                    processingStatus: `${FileProcessingStatus.Unprocessed}`,
                    tenantCode: tenantCode,
                    serviceCode: serviceCode
                };
    
                const options: UploadOptions = {
                    onSuccess: (res) => {
                        let accessUrl = "";
                        if (res.getHeader("asset-url")) {
                            accessUrl = res.getHeader("asset-url");
                        }
    
                        if (res.getHeader("asset-error")) {
                            onError(errorUploadMessage);
                        }
    
                        if (!Helpers.isNullOrEmpty(accessUrl)) {
                            editor.selection.insertImage(accessUrl, {"width": "100%", "height": "100%"}, "100%");
                        }
    
                        onHideLoading();
                    },
                    onError: (error: any) => {
                        onError(Helpers.renderExceptionError(error));
                    },
                };
    
                const tusUpload = ResumableUploadService.upload(file, request, options);
                tusUpload.start();
            } catch (error) {
                console.log(error)
                onError(Helpers.renderExceptionError(error));
            }
        }
    }

    return (
        <Box>
            <Box>
                <Typography {...typographyProps}>{`${props.label} ${props.required ? "*" : ""}`}</Typography>
            </Box>

            <Suspense fallback="Loading rich text input...">
                <JoditEditor
                    ref={editor}
                    config={config}
                    value={defaultValue}
                    onBlur={(newContent: string) => {
                        Helpers.isFunction(props.onBlur) && props.onBlur(newContent);
                    }}
                />
            </Suspense>
            {!Helpers.isNullOrEmpty(props.errorMessage) && (
                <Box>
                    <Typography variant="caption" color="error">
                        {props.errorMessage}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

// TRACE CHANGED PROPS OF COMPONENT
function useTraceUpdate (props: any) {
    const prev = useRef(props);
    useEffect(() => {
        const changedProps = Object.entries(props).reduce((ps: any, [k, v]) => {
            if (prev.current[k] !== v) {
                ps[k] = [prev.current[k], v];
            }
            return ps;
        }, {});
        if (Object.keys(changedProps).length > 0) {
            console.log("Changed props:", changedProps);
        }
        prev.current = props;
    });
}

export default memo(InputHtmlUploadEditor);